/** Normalize reference-session tape for side-by-side analog comparison. */

export type AnalogTapeStats = {
  SPY?: number;
  QQQ?: number;
  XLK?: number;
  USO?: number;
  VIX?: number;
  y10?: number;
};

export type TapeFingerprintMetric = {
  id: string;
  label: string;
  today: number | null;
  analog: number | null;
  mode: 'pct' | 'level';
  /** Max bar scale for pct rows (symmetric around zero). */
  scale?: number;
  aligned: boolean;
  delta: number | null;
};

const PCT_METRICS: { id: keyof AnalogTapeStats; label: string; scale: number }[] = [
  { id: 'SPY', label: 'SPY', scale: 1.5 },
  { id: 'QQQ', label: 'QQQ', scale: 1.5 },
  { id: 'XLK', label: 'XLK', scale: 2 },
  { id: 'USO', label: 'Oil', scale: 5 },
];

const LEVEL_METRICS: { id: keyof AnalogTapeStats; label: string; scale: number; tolerance: number }[] = [
  { id: 'VIX', label: 'VIX', scale: 30, tolerance: 2.5 },
  { id: 'y10', label: '10y', scale: 5.5, tolerance: 0.2 },
];

function num(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number.parseFloat(value.replace('%', ''));
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/** Map API `market_overlay` or similar into comparable tape stats. */
export function normalizeReferenceTape(
  overlay: Record<string, unknown> | null | undefined,
): AnalogTapeStats {
  if (!overlay) return {};
  return {
    SPY: num(overlay.SPY) ?? undefined,
    QQQ: num(overlay.QQQ) ?? undefined,
    XLK: num(overlay.Technology_XLK) ?? num(overlay.XLK) ?? undefined,
    USO: num(overlay.oil_uso_pct) ?? num(overlay.USO) ?? undefined,
    VIX: num(overlay.VIX) ?? undefined,
    y10: num(overlay.ten_year_yield_pct) ?? num(overlay.y10) ?? num(overlay.yield_10y) ?? undefined,
  };
}

/** Map match `dayStats` / session block into comparable tape stats. */
export function normalizeAnalogTape(
  stats: Record<string, unknown> | null | undefined,
): AnalogTapeStats {
  if (!stats) return {};
  return {
    SPY: num(stats.SPY) ?? undefined,
    QQQ: num(stats.QQQ) ?? undefined,
    XLK: num(stats.XLK) ?? num(stats.tech_sector_pct) ?? undefined,
    USO: num(stats.USO) ?? num(stats.oil) ?? undefined,
    VIX: num(stats.VIX) ?? num(stats.vix) ?? undefined,
    y10: num(stats.y10) ?? num(stats.yield_10y) ?? num(stats['yield_10y']) ?? undefined,
  };
}

function pctAligned(today: number, analog: number, tolerance = 0.35): boolean {
  if (Math.sign(today) !== Math.sign(analog) && Math.abs(today) > 0.08 && Math.abs(analog) > 0.08) {
    return false;
  }
  return Math.abs(today - analog) <= tolerance;
}

function levelAligned(today: number, analog: number, tolerance: number): boolean {
  return Math.abs(today - analog) <= tolerance;
}

export function buildTapeFingerprint(
  reference: AnalogTapeStats,
  analog: AnalogTapeStats,
): TapeFingerprintMetric[] {
  const rows: TapeFingerprintMetric[] = [];

  for (const { id, label, scale } of PCT_METRICS) {
    const today = reference[id] ?? null;
    const analogVal = analog[id] ?? null;
    if (today == null && analogVal == null) continue;
    const t = today ?? 0;
    const a = analogVal ?? 0;
    rows.push({
      id,
      label,
      today: today,
      analog: analogVal,
      mode: 'pct',
      scale,
      aligned: today != null && analogVal != null ? pctAligned(t, a) : false,
      delta: today != null && analogVal != null ? analogVal - today : null,
    });
  }

  for (const { id, label, scale, tolerance } of LEVEL_METRICS) {
    const today = reference[id] ?? null;
    const analogVal = analog[id] ?? null;
    if (today == null && analogVal == null) continue;
    rows.push({
      id,
      label,
      today,
      analog: analogVal,
      mode: 'level',
      scale,
      aligned: today != null && analogVal != null ? levelAligned(today, analogVal, tolerance) : false,
      delta: today != null && analogVal != null ? analogVal - today : null,
    });
  }

  return rows;
}

export function barWidthPct(value: number, scale: number): number {
  return Math.min(100, (Math.abs(value) / scale) * 100);
}
